import { FileExtended } from "ui/components/FileUpload/FileDropZone/types";
import { FleetDocumentUpload } from "../fleet/vehicles/documents/types";
import {
  Contact,
  ContactAddress,
  ContactAddressEditFormValues,
  ContactAddressRow,
  ContactEditFormValues,
  ContactEmail,
  ContactEmailEditFormValues,
  ContactEmailRow,
  ContactNote,
  ContactNoteEditFormValues,
  ContactNoteRow,
  ContactPhone,
  ContactPhoneEditFormValues,
  ContactPhoneRow,
  ContactRow,
  ContactType,
  ContactFile,
  ContactGroupRow,
  ContactGroupEditFormValues,
  ContactImportXlsFormValues,
  ContactImportXlsResult,
  ContactPictureFormValues,
} from "./types";

/*---------------------------------------------------------------------------------------
    CONTACT SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformContactRow = (res: any): ContactRow => {

  return {
    id: res.id,
    contactType: res.contactType,
    contactGroups: res.contactGroups ?? [],
    name: res.name,
    firstName: res.firstName,
    lastName: res.lastName,
    companyName: res.companyName,
    department: res.department,
    jobTitle: res.jobTitle,
    primaryContactEmail: res.primaryContactEmail,
    primaryContactPhone: res.primaryContactPhone,
    primaryContactAddress: res.primaryContactAddress,
    pictures: res.pictures,
    childRows: res.representatives
      ? res.representatives.map((r: any) => ({
          id: r.id,
          personContactId: r.personContact?.id,
          name: r.personContact?.name,
          firstName: r.personContact?.firstName,
          lastName: r.personContact?.lastName,
          jobTitle: r.personContact?.jobTitle,
          department: r.personContact?.department,
          email: r.personContact?.primaryContactEmail?.emailAddress,
          phone: r.personContact?.primaryContactPhone?.phoneNumber,
          address: r.personContact?.primaryContactAddress?.line1,
          groups: r.personContact?.contactGroups ?? [],
          pictureUrl: r.personContact?.pictures?.[0]?.url ?? null,
        }))
      : [],
  };
};

/*---------------------------------------------------------------------------------------
    CONTACT CREATE
  -----------------------------------------------------------------------------------------*/

export const transformContactOnCreate = (
  formValues: ContactEditFormValues,
  uploads: FleetDocumentUpload[]
): any => ({
  firstName: formValues.firstName,
  lastName: formValues.lastName,
  jobTitle: formValues.jobTitle,
  department: formValues.department,
  contactTypeId: formValues.contactTypeId,
  contactGroupIds: formValues.contactGroupIds,
  companyName: formValues.companyName,
  personalIdentificationNumber: formValues.personalIdentificationNumber,
  uploads: uploads,
});
/*---------------------------------------------------------------------------------------
      CONTACT EDIT
    -----------------------------------------------------------------------------------------*/
export const transformContactOnUpdate = (
  formValues: ContactEditFormValues,
  uploads: FleetDocumentUpload[]
): any => ({
  id: formValues.id,
  firstName: formValues.firstName,
  lastName: formValues.lastName,
  jobTitle: formValues.jobTitle,
  department: formValues.department,
  contactTypeId: formValues.contactTypeId,
  contactGroupIds: formValues.contactGroupIds,
  companyName: formValues.companyName,
  personalIdentificationNumber: formValues.personalIdentificationNumber,
  uploads: uploads,
});

/*---------------------------------------------------------------------------------------
      CONTACT GET
    -----------------------------------------------------------------------------------------*/
export const transformContact = (res: any): Contact => {
  return {
    id: res.id,
    name: res.name,
    firstName: res.firstName,
    lastName: res.lastName,
    jobTitle: res.jobTitle,
    department: res.department,
    contactType: res.contactType,
    contactGroups: res.contactGroups ?? [],
    companyName: res.companyName,
    personalIdentificationNumber: res.personalIdentificationNumber,
    primaryContactEmail: res.primaryContactEmail ?? null,
    primaryContactPhone: res.primaryContactPhone ?? null,
    primaryContactAddress: res.primaryContactAddress ?? null,
    pictures: res.pictures,
  };
};


export const transformContactEditFormValues = (
  res: Contact
): ContactEditFormValues => {

  // Group images by name
  const picturesMap = new Map<string, FileExtended[]>();

  if (res.pictures && Array.isArray(res.pictures)) {
    res.pictures.forEach((u) => {
      const contentType = u.contentType || "image/webp";
      const originalFileName = u.originalFileName || `${u.id}.webp`;
      const created = u.created ? new Date(u.created).getTime() : Date.now();

      const dummyBlob = new Blob([""], { type: contentType });

      const fakeFile: FileExtended = Object.assign(
        new File([dummyBlob], originalFileName, {
          type: contentType,
          lastModified: created,
        }),
        {
          id: u.id,
          url: u.url,
          isNew: false,
          originalFileName: u.originalFileName,
          fileExtension: u.fileExtension,
          fileSizeKb: u.fileSizeKb,
          contentType: u.contentType,
          created: u.created,
        }
      );

      const pictureName = u.name || "Bez naziva";
      if (!picturesMap.has(pictureName)) {
        picturesMap.set(pictureName, []);
      }
      picturesMap.get(pictureName)!.push(fakeFile);
    });
  }

  // Convert map to array of ContactPictureFormValues
  const pictures: ContactPictureFormValues[] = Array.from(picturesMap.entries()).map(
    ([name, files]) => ({ name, files })
  );

  return {
    id: res.id,
    firstName: res.firstName,
    lastName: res.lastName,
    jobTitle: res.jobTitle,
    department: res.department,
    contactTypeId: res.contactType?.id ?? "",
    contactGroupIds: res.contactGroups?.map((g: any) => g.id) ?? [],
    companyName: res.companyName,
    personalIdentificationNumber: res.personalIdentificationNumber,
    pictures,
  };
};

/*---------------------------------------------------------------------------------------
    CONTACT TYPE GET
  -----------------------------------------------------------------------------------------*/
export const transformContactType = (res: any): ContactType => {
  return {
    id: res.id,
    name: res.name,
  };
};



/*---------------------------------------------------------------------------------------
  EMAIL SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformContactEmailRow = (res: any): ContactEmailRow => {
  return {
    id: res.id,
    emailAddress: res.emailAddress,
    description: res.description,
    primary: res.primary,
  };
};

/*---------------------------------------------------------------------------------------
    EMAIL CREATE
    -----------------------------------------------------------------------------------------*/

export const transformContactEmailOnCreate = (
  formValues: ContactEmailEditFormValues
): any => ({
  emailAddress: formValues.emailAddress,
  description: formValues.description,
  primary: formValues.primary,
});

/*---------------------------------------------------------------------------------------
    EMAIL UPDATE
    -----------------------------------------------------------------------------------------*/

export const transformContactEmailOnUpdate = (
  formValues: ContactEmailEditFormValues
): any => ({
  emailAddress: formValues.emailAddress,
  description: formValues.description,
  primary: formValues.primary,
});

/*---------------------------------------------------------------------------------------
    EMAIL GET
    -----------------------------------------------------------------------------------------*/

export const transformContactEmail = (res: any): ContactEmail => {
  return {
    id: res.id,
    emailAddress: res.emailAddress,
    description: res.description,
    primary: res.primary,
  };
};

/*---------------------------------------------------------------------------------------
   PHONE SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformContactPhoneRow = (res: any): ContactPhoneRow => {
  return {
    id: res.id,
    phoneTypeId: res.phoneTypeId,
    phoneNumber: res.phoneNumber,
    description: res.description,
    primary: res.primary,
  };
};

/*---------------------------------------------------------------------------------------
   PHONE CREATE
  -----------------------------------------------------------------------------------------*/

export const transformContactPhoneOnCreate = (
  formValues: ContactPhoneEditFormValues
): any => ({
  phoneTypeId: formValues.phoneTypeId,
  phoneNumber: formValues.phoneNumber,
  description: formValues.description,
  primary: formValues.primary,
});

/*---------------------------------------------------------------------------------------
   PHONE UPDATE
  -----------------------------------------------------------------------------------------*/
export const transformContactPhoneOnUpdate = (
  formValues: ContactPhoneEditFormValues
): any => ({
  id: formValues.id,
  phoneTypeId: formValues.phoneTypeId,
  phoneNumber: formValues.phoneNumber,
  description: formValues.description,
  primary: formValues.primary,
});

/*---------------------------------------------------------------------------------------
   PHONE GET
  -----------------------------------------------------------------------------------------*/
export const transformContactPhone = (res: any): ContactPhone => {
  return {
    id: res.id,
    phoneTypeId: res.phoneTypeId,
    phoneNumber: res.phoneNumber,
    description: res.description,
    primary: res.primary,
  };
};

/*---------------------------------------------------------------------------------------
    ADDRESS SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformContactAddressRow = (res: any): ContactAddressRow => {
  return {
    id: res.id,
    city: res.city,
    country: res.country,
    line1: res.line1,
    line2: res.line2,
    postalCode: res.postalCode,
    state: res.state,
    description: res.description,
    primary: res.primary,
    latitude: res.latitude,
    longitude: res.longitude
  };
};

/*---------------------------------------------------------------------------------------
    ADDRESS CREATE
  -----------------------------------------------------------------------------------------*/
export const transformContactAddressOnCreate = (
  formValues: ContactAddressEditFormValues
): any => ({
  city: formValues.city,
  country: formValues.country,
  line1: formValues.line1,
  line2: formValues.line2,
  postalCode: formValues.postalCode,
  state: formValues.state,
  description: formValues.description,
  primary: formValues.primary,
  latitude: formValues.latitude,
  longitude: formValues.longitude
});

/*---------------------------------------------------------------------------------------
    ADDRESS UPDATE
  -----------------------------------------------------------------------------------------*/

export const transformContactAddressOnUpdate = (
  formValues: ContactAddressEditFormValues
): any => ({
  id: formValues.id,
  city: formValues.city,
  country: formValues.country,
  line1: formValues.line1,
  line2: formValues.line2,
  postalCode: formValues.postalCode,
  state: formValues.state,
  description: formValues.description,
  primary: formValues.primary,
  latitude: formValues.latitude,
  longitude: formValues.longitude
});

/*---------------------------------------------------------------------------------------
   ADDRESS GET
  -----------------------------------------------------------------------------------------*/
export const transformContactAddress = (res: any): ContactAddress => {
  return {
    id: res.id,
    city: res.city,
    country: res.country,
    line1: res.line1,
    line2: res.line2,
    postalCode: res.postalCode,
    state: res.state,
    description: res.description,
    primary: res.primary,
    latitude: res.latitude,
    longitude: res.longitude
  };
};

/*---------------------------------------------------------------------------------------
    NOTE SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformContactNoteRow = (res: any): ContactNoteRow => {
  return {
    id: res.id,
    content: res.content,
    important: res.important,
    created: res.created,
  };
};

/*---------------------------------------------------------------------------------------
    NOTE CREATE
  -----------------------------------------------------------------------------------------*/

export const transformContactNoteOnCreate = (
  formValues: ContactNoteEditFormValues
): any => ({
  content: formValues.content,
  important: formValues.important,
});

/*---------------------------------------------------------------------------------------
    NOTE UPDATE
  -----------------------------------------------------------------------------------------*/

export const transformContactNoteOnUpdate = (
  formValues: ContactNoteEditFormValues
): any => ({
  id: formValues.id,
  content: formValues.content,
  important: formValues.important,
});

/*---------------------------------------------------------------------------------------
   NOTE GET
  -----------------------------------------------------------------------------------------*/

export const transformContactNote = (res: any): ContactNote => {
  return {
    id: res.id,
    content: res.content,
    important: res.important,
    created: res.created,
  };
};

/*---------------------------------------------------------------------------------------
    CONTACT CREATE IMPORT
  -----------------------------------------------------------------------------------------*/
export const transformContactImportXlsOnCreate = (
  formValues: ContactImportXlsFormValues
): any => ({
  firstName: formValues.name,
  name: formValues.files,
});

export const transformContactImportXlsResult = (
  res: any
): ContactImportXlsResult => {
  return {
    id: res.id,
    department: res.department,
    firstName: res.firstName,
    lastName: res.lastName,
    email: res.email,
    phone: res.phone,
  };
};

/*---------------------------------------------------------------------------------------
    CONTACT IMPORT GET
-----------------------------------------------------------------------------------------*/

export const transformContactImport = (res: any): ContactFile => {
  return {
    id: res.id,
    fileName: res.fileName,
    fileHandler: res.fileHandler,
    fileExtension: res.fileExtension,
    originalName: res.originalName,
  };
};

/*---------------------------------------------------------------------------------------
    CONTACT GROUP SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformContactGroupRow = (res: any): ContactGroupRow => {
  return {
    id: res.id,
    name: res.name,
  };
};

/*---------------------------------------------------------------------------------------
      CONTACT CREATE
    -----------------------------------------------------------------------------------------*/

export const transformContactGroupOnCreate = (
  formValues: ContactGroupEditFormValues
): any => ({
  name: formValues.name,
});
